import React from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { CircularProgress } from "@mui/material";
import useAuthStore from "../../../Zustand/Store/useAuthStore";
import usePermissionDataStore from "../../../Zustand/Store/usePermissionDataStore";
import { MAIN_URL } from "../../../Configurations/Urls";
import { emsBatchListRowMatchesLoggedInTrainer } from "../../../Utils/emsTrainerUserMatch";
import { seesAllBatches } from "../../../Utils/trainerPortal";

function n(val) {
  const x = Number(val);
  return Number.isFinite(x) ? x : 0;
}

function fmtPct(val) {
  if (val === null || val === undefined || val === "") return "—";
  const x = Number(val);
  if (!Number.isFinite(x)) return "—";
  return `${x.toFixed(1)}%`;
}

function pctClass(val) {
  if (val === null || val === undefined || val === "") return "";
  const x = Number(val);
  if (!Number.isFinite(x)) return "";
  if (x >= 75) return "ok";
  if (x >= 50) return "teal";
  return "bad";
}

function extractList(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  return [];
}

export default function StudentAttendanceReport() {
  const { userData } = useAuthStore();
  const Permission = usePermissionDataStore((state) => state.Permission);
  const organizationId = userData?.organization?.organization_id;
  const selfOnly = React.useMemo(() => {
    const has = (action) =>
      Array.isArray(Permission) &&
      Permission.some((item) => String(item).trim().toUpperCase() === action);
    return has("VIEW_SELF_CLASS_ATTENDANCE") && !has("VIEW_CLASS_ATTENDANCES");
  }, [Permission]);

  const [batches, setBatches] = React.useState([]);
  const [batchId, setBatchId] = React.useState("");
  const [attendanceBand, setAttendanceBand] = React.useState("all");
  const [studentStatus, setStudentStatus] = React.useState("active");
  const [loading, setLoading] = React.useState(false);
  const [errorText, setErrorText] = React.useState("");
  const [rows, setRows] = React.useState([]);

  const authHeaders = React.useMemo(() => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  React.useEffect(() => {
    if (!organizationId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${MAIN_URL}/api/ems/batches`, {
          headers: authHeaders,
          params: { organization_id: organizationId, per_page: "all" },
        });
        const list = extractList(res?.data?.data).length
          ? extractList(res?.data?.data)
          : extractList(res?.data);
        const normalized = list
          .map((b) => ({
            id: b.organization_ems_batch_id ?? b.id ?? null,
            code: b.batch_code || "",
            name: b.batch_name || "",
            batch_trainers: b.batch_trainers || b.batchTrainers || [],
          }))
          .filter((b) => b.id != null)
          .sort((a, b) =>
            String(a.code || a.name).localeCompare(String(b.code || b.name))
          );
        if (!cancelled) setBatches(normalized);
      } catch {
        if (!cancelled) setBatches([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [organizationId, authHeaders]);

  const fetchReport = React.useCallback(async () => {
    if (!organizationId) return;
    setLoading(true);
    setErrorText("");
    try {
      const body = {
        organization_id: organizationId,
      };
      if (batchId) body.organization_ems_batch_id = Number(batchId);
      body.student_status = studentStatus === "inactive" ? "inactive" : "active";

      if (attendanceBand === "low") {
        body.min_percent = 0;
        body.max_percent = 49.999;
      } else if (attendanceBand === "medium") {
        body.min_percent = 50;
        body.max_percent = 74.999;
      } else if (attendanceBand === "high") {
        body.min_percent = 75;
        body.max_percent = 100;
      } else if (attendanceBand === "perfect") {
        body.min_percent = 100;
        body.max_percent = 100;
      }

      const res = await axios.post(
        `${MAIN_URL}/api/ems/classes-attendance/student-report`,
        body,
        { headers: authHeaders }
      );
      setRows(Array.isArray(res?.data?.data) ? res.data.data : []);
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Failed to fetch attendance report";
      setErrorText(msg);
      setRows([]);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [organizationId, batchId, attendanceBand, studentStatus, authHeaders]);

  React.useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  return (
    <div className="ems-dashboard ems-fade-up">
      <div className="ems-page-head">
        <div>
          <h1 className="ems-page-title">Attendance Report</h1>
        </div>
        <div className="ems-head-actions" style={{ flexWrap: "wrap", gap: 8 }}>
          <div className="ems-seg" role="group" aria-label="Student status">
            <button
              type="button"
              className={studentStatus === "active" ? "active" : ""}
              onClick={() => setStudentStatus("active")}
            >
              Active
            </button>
            <button
              type="button"
              className={studentStatus === "inactive" ? "active" : ""}
              onClick={() => setStudentStatus("inactive")}
            >
              Inactive
            </button>
          </div>
          <select
            className="ems-select"
            value={batchId}
            onChange={(e) => setBatchId(e.target.value)}
            aria-label="Filter by batch"
            style={{ minWidth: 200 }}
          >
            <option value="">All batches</option>
            {(selfOnly && !seesAllBatches(Permission, userData)
              ? batches.filter((b) => emsBatchListRowMatchesLoggedInTrainer(b, userData))
              : batches
            ).map((b) => (
              <option key={b.id} value={b.id}>
                {b.code || b.name || `Batch #${b.id}`}
              </option>
            ))}
          </select>
          <select
            className="ems-select"
            value={attendanceBand}
            onChange={(e) => setAttendanceBand(e.target.value)}
            aria-label="Attendance filter"
            style={{ minWidth: 200 }}
          >
            <option value="all">All attendance</option>
            <option value="low">Low (&lt; 50%)</option>
            <option value="medium">Medium (50% – 74%)</option>
            <option value="high">High (≥ 75%)</option>
            <option value="perfect">Perfect (100%)</option>
          </select>
        </div>
      </div>

      <div className="ems-section-divider" />

      {errorText ? (
        <div style={{ color: "var(--ems-red)", fontSize: 13, marginBottom: 12 }}>
          {errorText}
        </div>
      ) : null}

      <div className="ems-card ems-table-card">
        {loading ? (
          <div className="ems-loading-screen">
            <CircularProgress size={28} />
          </div>
        ) : (
          <div className="ems-table-wrap">
            <table className="ems-data">
              <thead>
                <tr>
                  <th>Student ID</th>
                  <th>Student</th>
                  <th>Batch Code</th>
                  <th>Training Program</th>
                  <th>Trainer</th>
                  <th className="ems-num">Total Batch Classes</th>
                  <th className="ems-num">Present</th>
                  <th className="ems-num">Absent</th>
                  <th className="ems-num" style={{ width: 110 }}>
                    Attendance %
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={9} style={{ textAlign: "center", opacity: 0.7 }}>
                      No enrollments found for the selected filters.
                    </td>
                  </tr>
                ) : (
                  rows.map((row) => {
                    const pct = row.attendance_percent;
                    const tone = pctClass(pct);
                    return (
                      <tr
                        key={`${row.organization_ems_batch_student_id}-${row.organization_ems_student_id}-${row.organization_ems_batch_id}`}
                      >
                        <td style={{ whiteSpace: "nowrap" }}>
                          {row.student_id || "—"}
                        </td>
                        <td style={{ whiteSpace: "nowrap" }}>
                          {row.student_name || "—"}
                        </td>
                        <td style={{ whiteSpace: "nowrap" }}>
                          {row.batch_code || "—"}
                        </td>
                        <td style={{ whiteSpace: "nowrap" }}>
                          {row.training_program_name || "—"}
                        </td>
                        <td style={{ whiteSpace: "nowrap" }}>
                          {row.trainer_name || "—"}
                        </td>
                        <td className="ems-num ems-tnum">
                          {n(row.total_batch_classes)}
                        </td>
                        <td className="ems-num ems-tnum">{n(row.present)}</td>
                        <td className="ems-num ems-tnum">{n(row.absent)}</td>
                        <td className={`ems-num ems-tnum ${tone}`}>
                          {fmtPct(pct)}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
